import * as PIXI from "pixi.js";
import DrawingManagerRenderer from "./DrawingManagerRenderer";
import BaseListener from "./BaseListener";
import { DrawingTool, QuestionType } from "../../../types/Enum";
import { GridConfig, Coord } from "../../../../../../../src/types/Interface";
import getSnap from "./helpers/getSnap.ts";
import DrawingFactory from "./DrawingFactory.ts";
import { EnumEventBus, eventBus } from "../../EventBus.ts";
import TestQuestionManager from "../../TestQuestionManager/TestQuestionManager.ts";
import Line from "../../../../../../../src/entities/Line/Line.ts";
import Ruler from "../../../../../../../src/entities/Tools/Ruler/Ruler.ts";
import Protractor from "../../../../../../../src/entities/Tools/Protractor/Protractor.ts";
import Compass from "../../../../../../../src/entities/Tools/Compass/Compass.ts";
import { colorOptEventBus, EnumColorOptEventBus } from "../../../../../../../src/entities/UI/ColorOptionCon/ColorOptEventBus.ts";
import { cursorEventBus, EnumCursorEventBus } from "../../../../../../../src/entities/UI/CustomCursor/CustomCursorEventBus.ts";
import Arc from "../../../../../../../src/entities/Arc.ts";


export default class DrawingManager extends BaseListener {
  private DrawingRenderer = new DrawingManagerRenderer();
  private TestQuestionManager = TestQuestionManager.getInstance();
  private selectedTool: DrawingTool = DrawingTool.empty;
  private isStarted = false;
  private coordStart: Coord = { x: 0, y: 0 };
  private coordEnd: Coord = { x: 0, y: 0 };
  private isAnswer: boolean = false;
  private answer: { coord: Coord[], id: number }[] = [];
  private Ruler = new Ruler((line: Line) => this.DrawingRenderer.addExtraDrawing(line));
  private protractor = new Protractor();
  private Compass = new Compass((arc: Arc) => { this.DrawingRenderer.addExtraDrawing(arc) });

  constructor(private GridConfig: GridConfig) {
    super();
    this.Ruler.setVisible(false);
    this.protractor.setVisible(false);
    this.Compass.setVisible(false)

    eventBus.on(EnumEventBus.clearAll, () => this.deleteAllElms());
    eventBus.on(EnumEventBus.delLastDrawing, () => this.deletelastElm());
    eventBus.on(EnumEventBus.setQuestMode, () => this.DrawingRenderer.clearAll());
    eventBus.on(EnumEventBus.startAnswer, (v: boolean) => this.startAnswer(v));
    eventBus.on(EnumEventBus.saveAnswer, () => this.saveAnswer());
    eventBus.on(EnumEventBus.showTestQuestion, (i: number) => this.drawAnswer(i));
    eventBus.on(EnumEventBus.showRuler, (v: boolean) => this.Ruler.setVisible(v));
    eventBus.on(EnumEventBus.showProtractor, (v: boolean) => this.protractor.setVisible(v));
    eventBus.on(EnumEventBus.showCompass, (v: boolean) => this.Compass.setVisible(v));

    colorOptEventBus.on(EnumColorOptEventBus.selectedColor, (color: number) => this.setSelectedColor(color));
  }

  private isEmpty = () => this.selectedTool === DrawingTool.empty;
  private isPointTool = () => this.selectedTool.includes("POINT");
  private isLineTool = () => this.selectedTool.includes("LINE");
  private isPencilTool = () => this.selectedTool.includes("PENCIL");
  private setSelectedColor(color: number) {
    this.DrawingRenderer.setDrawingColor(color);
    cursorEventBus.emit(EnumCursorEventBus.setColor, color);
    this.Compass.setColor(color);
    this.Ruler.setColor(color);
  }
  private startAnswer(v: boolean) {
    this.isAnswer = v;
    this.coordStart = this.coordEnd = { x: 0, y: 0 }
  }
  private saveAnswer() {
    this.TestQuestionManager.addAnswer(this.answer);
    this.answer = [];
    this.isAnswer = false;
    this.DrawingRenderer.clearDrawing();
  }
  setSelectedTool(tool: DrawingTool, color: number) {
    this.selectedTool = tool;
    this.DrawingRenderer.setSelectedTool(this.selectedTool);
    const isPencil = this.isPencilTool();
    if (this.isLineTool() || isPencil || this.isPointTool()) {
      const bg = this.DrawingRenderer.drawBg();
      DrawingFactory.addBgInteractivity(bg, (coord: Coord) => this.onPointerDown(coord), isPencil);
    }
    else if (this.isEmpty) this.DrawingRenderer.clearBg();
  }
  protected onPointerDown(coord: Coord): void {
    if (this.isEmpty()) null;
    else if (this.isPointTool()) {
      this.DrawingRenderer.addPoint(getSnap(this.GridConfig, this.GridConfig.cellSize, coord));
      if (this.isAnswer) {
        const drawing = this.DrawingRenderer.getDrawing();
        const ans = { coord: [{ x: drawing.graph.x, y: drawing.graph.y }], id: drawing.id };
        this.answer.push(ans);
      }
    } else {
      this.isStarted = true;
      this.coordStart = this.coordEnd = coord;

      if (this.isPencilTool()) this.DrawingRenderer.initializePencilLine()
      else {
        this.coordStart = getSnap(this.GridConfig, this.GridConfig.cellSize, coord);
        this.DrawingRenderer.initializeLine();
      };
    }
  }
  protected onPointerMove(coord: Coord): void {
    if (!this.isStarted || !this.DrawingRenderer.getDrawing()) { return };
    if (this.isLineTool()) {
      this.coordEnd = getSnap(this.GridConfig, this.GridConfig.cellSize, coord);
      this.DrawingRenderer.drawLine(this.coordStart, this.coordEnd);
    } else if (this.isPencilTool()) {
      this.coordEnd = coord;
      this.DrawingRenderer.drawLine(this.coordStart, this.coordEnd);
      this.coordStart = coord;
    }
  }
  protected onPointerUp = (): void => {
    if (!this.isStarted) return;
    this.addAnswer();
    this.isStarted = false;
  };
  addAnswer() {
    if (this.coordStart.x === this.coordEnd.x && this.coordStart.y === this.coordEnd.y) { return };
    const drawing = this.DrawingRenderer.getDrawing();
    if (drawing && drawing.graph instanceof Line && this.isAnswer) {
      this.answer.push({ coord: [this.coordStart, this.coordEnd], id: drawing.id });
    }
  }
  private deletelastElm() {
    const id = this.DrawingRenderer.delLastDrawing();
    id ? this.answer = this.answer.filter(a => a.id !== id) : null;
  }
  private deleteAllElms() {
    this.DrawingRenderer.clearAll();
    this.answer = [];
  }
  private drawAnswer(index: number) {
    this.DrawingRenderer.clearAll();
    const answer = this.TestQuestionManager.getAnswer(index);
    if (answer.type === QuestionType.TYPE1) {
      this.DrawingRenderer.setSelectedTool(DrawingTool.answerLine);
      answer.answer.forEach(a => {
        this.DrawingRenderer.initializeLine();
        this.DrawingRenderer.drawLine(a.coord[0], a.coord[1])
      });
      return
    }
    this.DrawingRenderer.setSelectedTool(DrawingTool.answerPoint);
    answer.answer.forEach(a => this.DrawingRenderer.addPoint(a.coord[0]));
  }
}